import { BadgeCheck } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Premium grid-card layout for every board / exam / grade we cover.
 * - 2 cols mobile · 3 cols sm · 4 cols lg · 5 cols xl
 * - Equal-height cards via flex column + h-full
 * - Consistent padding, centred icon + label, no truncate, clean wrap
 * - Subtle hover lift + amber accent
 */
const PILLS = [
    { slug: "cbse",           letter: "C", label: "CBSE",                  sub: "Class 1–12" },
    { slug: "icse-isc",       letter: "I", label: "ICSE & ISC",            sub: "Class 1–12" },
    { slug: "ib",             letter: "I", label: "IB",                    sub: "PYP · MYP · DP" },
    { slug: "igcse",          letter: "I", label: "IGCSE",                 sub: "Cambridge International" },
    { slug: "a-level",        letter: "A", label: "A-Level",               sub: "AS & A2" },
    { slug: "ap",             letter: "A", label: "AP",                    sub: "Advanced Placement" },
    { slug: "state-boards",   letter: "S", label: "State Boards",          sub: "MP · MH · UP & More" },
    { slug: "neet",           letter: "N", label: "NEET Preparation",      sub: "Biology · Physics · Chemistry" },
    { slug: "jee",            letter: "J", label: "JEE Main & Advanced",   sub: "Maths · Physics · Chemistry" },
    { slug: "ielts-toefl",    letter: "I", label: "IELTS & TOEFL",         sub: "Speaking · Writing · Listening" },
    { slug: "spoken-english", letter: "S", label: "Spoken English",        sub: "Communication Skills" },
    { slug: "coding",         letter: "C", label: "Coding",                sub: "Computer Courses" },
];

export default function TrustPills() {
    return (
        <section
            data-testid="home-trust-pills"
            className="relative py-14 sm:py-16 lg:py-20 bg-white border-t border-slate-100"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="text-center mb-10 sm:mb-12">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-700 mb-4">
                        <BadgeCheck className="w-3 h-3" /> Specialists for every exam &amp; board
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl lg:text-[2.5rem] font-bold text-[#0F172A] leading-tight">
                        Every board. Every exam. Every grade.
                    </h2>
                    <p className="mt-3 text-sm sm:text-base text-slate-600 max-w-2xl mx-auto">
                        From CBSE primary to IB Diploma and competitive exams — pick your curriculum and we'll match a subject-specialist 1:1 tutor.
                    </p>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 max-w-6xl mx-auto">
                    {PILLS.map((p) => (
                        <Link
                            key={p.slug}
                            to={`/courses/${p.slug}`}
                            data-testid={`trust-pill-${p.slug}`}
                            aria-label={`Learn more about ${p.label} tuition`}
                            className="group h-full flex flex-col items-center text-center bg-white border border-slate-200 rounded-2xl px-4 py-5 sm:py-6 hover:border-[#1E3A8A] hover:shadow-lg hover:-translate-y-1 hover:bg-gradient-to-br hover:from-white hover:to-blue-50/40 transition-all duration-200"
                        >
                            <div className="w-11 h-11 sm:w-12 sm:h-12 shrink-0 rounded-xl bg-[#1E3A8A] text-white flex items-center justify-center text-base sm:text-lg font-heading font-bold mb-3 group-hover:bg-amber-500 group-hover:text-[#0F172A] group-hover:scale-105 transition-all duration-200">
                                {p.letter}
                            </div>
                            <div className="font-heading font-bold text-[#0F172A] text-sm sm:text-[15px] leading-snug">
                                {p.label}
                            </div>
                            <div className="mt-1 text-[11px] sm:text-xs text-slate-500 leading-snug">
                                {p.sub}
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
}
